// =============================================================================
// kioskBinding.js — "which shop is this tablet?", the kiosk half of Lane CB.
//
// PLAIN ENGLISH. Every tablet signs in as an anonymous visitor, and its login
// token says nothing about which shop it is standing in. So every shop-scoped
// feed has to take the tablet's word for it. This module lets an admin tap their
// PIN on a tablet and say "this one is Trafford". The database records it
// (migration 1460) and from then on that tablet's token carries a `site_key`
// claim, which migration 1461's feeds read: ask about another shop and you get
// an empty answer.
//
// GATED OFF. KIOSK_BIND_HOSTS is EMPTY, so nothing renders and nothing is called
// anywhere. `?kioskbind=on` turns it on for one tab, `?kioskbind=off` forces it
// off — the same pattern as stockWriteDropOverride in stockSync.js.
//
// ⚠️ HOW LONG A BINDING LASTS — READ THIS BEFORE FLIPPING A HOST.
// The shared Supabase client is created with `persistSession: false`
// (src/lib/supabaseClient.js), so the anonymous login lives in memory for the
// life of the PAGE. Reload the tablet — overnight restart, a crash, a deploy —
// and it signs in as a brand-new anonymous user with no binding, and the feeds
// go back to today's behaviour (they fail OPEN, never closed, so nothing breaks;
// the tablet just loses its badge until an admin binds it again).
//
// 🔑 THE SPARE KEY (Lane CL, 16 Sep 2026) — the second half of this file fixes
// that, for hosts that are switched on. When an admin binds a tablet on a DURABLE
// host, the database hands that tablet one long random password of its own and
// keeps only a scrambled copy (migration 1560); the tablet keeps the password in
// this browser's storage. On the next boot it shows the password, gets its badge
// straight back, and nobody has to tap a PIN. Unbind and the password dies with
// the binding. KIOSK_DURABLE_HOSTS is EMPTY, so on every host today this is a
// per-shift badge exactly as before and not one extra call is made.
//
// 🚫 WHY WE DID NOT JUST REMEMBER THE LOGIN (the obvious answer, and it is wrong).
// Turning on `persistSession` for the shared client would remember the PIN pass
// too. A pass is baked into the login token as claims and stays there until a new
// token is issued, and this app deliberately never issues one by itself
// (`autoRefreshToken: false`). So today, throwing the token away IS the auto-lock:
// reload the till and it re-locks. Remember the login and a reloaded till comes
// back still unlocked as whoever last tapped in, for the rest of that token's life
// (an hour on this project, against a 15-minute pass) — clock-ins, food-safety
// signatures and wastage entries recorded against someone who went home. The
// device password below changes nothing about how anyone logs in. Written up as
// CL-1 in OVERNIGHT/DECISIONS_NEEDED.md and in RUNBOOK_KIOSK_SITE_BINDING.md.
// (While checking that: JJ's Club Card scanner does NOT share this client — it
// talks to the Railway backend with its own token — so it is untouched either way.)
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { ensureAnonSession } from "./auth.js";
import { getActiveSiteKey } from "../config/sites.js";

// EMPTY BY DEFAULT — no shop, no behaviour change, nothing to revert. Turning a
// tablet's shop on is a reviewed one-line host add after the runbook's soak.
const KIOSK_BIND_HOSTS = new Set([
  // "trafford.eatrolled.com",   // ← one tablet at a time, after a shift's soak
]);

// The whole gate as a pure function, so the OFF path is testable without a
// browser. `query` is the value of the ?kioskbind= parameter (or null).
// Returns true only when this host is listed, or a tab explicitly asks for it.
export function decideKioskBind({ hostname = "", query = null } = {}) {
  if (query === "on") return true;
  if (query === "off") return false;
  return KIOSK_BIND_HOSTS.has(String(hostname || "").toLowerCase());
}

export function kioskBindEnabled() {
  if (typeof window === "undefined") return false; // node/tests: default off
  let q = null;
  try { q = new URLSearchParams(window.location.search).get("kioskbind"); } catch (e) {}
  return decideKioskBind({ hostname: window.location.hostname || "", query: q });
}

// The shop this tablet is bound to, read STRAIGHT OFF THE TOKEN — no database
// call, because the whole point is that the claim is what the feeds will see.
// Pure, so the test can feed it a hand-built token. Returns a lower-case site
// key, or null for any token that is missing, unreadable or unbound.
export function boundSiteFromToken(accessToken) {
  try {
    const body = String(accessToken || "").split(".")[1];
    if (!body) return null;
    const json = typeof atob === "function"
      ? atob(body.replace(/-/g, "+").replace(/_/g, "/"))
      : Buffer.from(body, "base64url").toString();
    const claims = JSON.parse(json);
    const key = String(claims.site_key ?? "").trim().toLowerCase();
    return key || null;
  } catch (e) {
    return null;
  }
}

// The status line, as a pure function so the wording is testable and the panel
// stays dumb. `boundSite` is what the token says; `siteKey`/`siteName` are the
// shop this tablet is CONFIGURED as (src/config/sites.js).
export function describeBinding({ boundSite = null, siteKey = "", siteName = "" } = {}) {
  const here = String(siteKey || "").trim().toLowerCase();
  const label = siteName || siteKey || "this shop";
  if (!boundSite) {
    return { state: "unbound", text: "This tablet is not bound to a shop yet.", tone: "warn" };
  }
  if (here && boundSite !== here) {
    return {
      state: "mismatch",
      text: `This tablet is bound to ${boundSite}, but it is set up as ${label} (${here}). It will show nothing until that is fixed.`,
      tone: "bad",
    };
  }
  return { state: "bound", text: `This tablet is bound to ${label}.`, tone: "good" };
}

// THE LOUD ONE. (External audit, 15 Sep 2026, point 1.) A tablet badged for one
// shop but set up as another shows EMPTY food-safety forms, an empty HACCP sheet and
// an empty orders strip — and, without this, no reason why. Silence is the worst
// possible answer there: staff assume the records are gone, or that nothing needs
// doing today. So the moment the token says one shop and the screen says another, the
// panel puts a red banner on the screen and one line in the browser console.
//
// Pure, so it can be tested without a browser: the token's badge in, a banner or
// nothing out. `boundSite` is what the JWT carries; `siteKey`/`siteName` are the shop
// this build is configured as (src/config/sites.js). No badge at all -> nothing (an
// unbound tablet is normal and fails open). Badge matches -> nothing.
export function decideSiteMismatch({ boundSite = null, siteKey = "", siteName = "" } = {}) {
  const badge = String(boundSite || "").trim().toLowerCase();
  const here = String(siteKey || "").trim().toLowerCase();
  if (!badge || !here || badge === here) return null;
  const configuredAs = siteName && String(siteName).trim() ? `${siteName} (${here})` : here;
  return {
    badgedAs: badge,
    configuredAs,
    text: `This tablet is badged as ${badge} but this screen is configured as ${configuredAs} — its shop feeds will be empty until it is re-bound.`,
  };
}

// Read the live session's binding. No database call.
export async function currentBoundSite({ getSession = () => supabase.auth.getSession() } = {}) {
  try {
    const { data } = await getSession();
    return boundSiteFromToken(data?.session?.access_token);
  } catch (e) {
    return null;
  }
}

// Bind this tablet. The database refuses unless the caller has an ADMIN PIN pass
// on this very tablet right now (migration 1460), so there is no need — and no
// point — in guessing at that here; we just report what it says.
//
// 🔑 THE REFRESH IS NOT OPTIONAL, AND IT IS THE ONLY ONE THERE IS.
// (External audit, 15 Sep 2026, point 2.) The kiosk's Supabase client is built with
// `autoRefreshToken: false` (src/lib/supabaseClient.js), so nothing in the background
// ever re-mints this tablet's login pass. The pass it is holding right now was minted
// before the binding existed, so it does not carry the badge — and it never will
// unless somebody asks for a new one. `refreshSession()` below IS that ask. Every
// path that binds or unbinds must call it, or the screen and the database disagree
// until the page is reloaded. Both paths below do; the test asserts it; and a refusal
// deliberately does NOT refresh, because nothing changed.
export async function bindThisTablet(
  siteKey,
  {
    note = null,
    rpc = (n, a) => supabase.rpc(n, a),
    refresh = () => supabase.auth.refreshSession(),
    // Lane CL. On a durable host, the bind is also the one moment the tablet can be
    // given its spare key — the database only opens that door for an admin PIN
    // tapped on this very tablet, which is exactly what we have just used. FALSE on
    // every host today, so this is dead code until somebody lists a host.
    durable = kioskDurableEnabled(),
    storage = undefined,
  } = {}
) {
  const key = String(siteKey || "").trim().toLowerCase();
  if (!key) return { ok: false, error: "No shop chosen." };
  try {
    const { data, error } = await rpc("kiosk_bind_site", { p: { site: key, ...(note ? { note } : {}) } });
    if (error) return { ok: false, error: friendlyError(error) };
    await refresh();                                   // pull a token the hook has stamped
    // Never allowed to spoil a good bind: it returns false rather than throwing, and
    // a tablet without a spare key is simply bound the way every tablet is today.
    let durableStored = false;
    if (durable) {
      durableStored = await issueAndStoreDeviceSecret({ rpc, ...(storage === undefined ? {} : { storage }) });
    }
    const bound = await currentBoundSite();
    return { ok: true, siteKey: data?.site_key || key, bound, durable, durableStored };
  } catch (e) {
    return { ok: false, error: friendlyError(e) };
  }
}

// Same rule as bindThisTablet: the refresh below is the ONLY thing that re-mints the
// pass (autoRefreshToken is false), so without it the tablet would keep showing — and
// keep being treated as — the shop it was just unbound from.
export async function unbindThisTablet(
  { rpc = (n, a) => supabase.rpc(n, a), refresh = () => supabase.auth.refreshSession(), storage = undefined } = {}
) {
  try {
    const { data, error } = await rpc("kiosk_unbind_site", {});
    if (error) return { ok: false, error: friendlyError(error) };
    await refresh();   // the one and only re-mint — see the note above bindThisTablet
    // Lane CL. Unbinding deletes the row, so the spare key is already dead in the
    // database — but a tablet must never walk around holding a key to a door it has
    // been thrown out of, so we drop the local copy too. Deliberately NOT gated on
    // the host: a host switched off between bind and unbind must still be cleaned.
    // On a host that never had a key this reads nothing and writes nothing.
    const cleared = clearDeviceSecret(storage === undefined ? {} : { storage });
    return { ok: true, removed: Number(data?.unbound || 0), secretCleared: cleared };
  } catch (e) {
    return { ok: false, error: friendlyError(e) };
  }
}

// 🔴 AN UNBIND THAT REMOVED NOTHING IS NOT A CLEAN UNBIND. (Review, 16 Sep 2026.)
// Unbinding only ever deletes the row sitting on THIS tablet's own login. If the
// shop's binding has moved onto another device — because somebody showed its device
// password there — the call still succeeds and removes zero rows, and every visible
// signal (the badge vanishing from the token, the panel going back to "not bound")
// says the tablet was unbound and its password is dead. It isn't. Silence there is
// the estate's signature failure, so say it out loud instead.
// Pure, so the wording is testable. `removed` is what the database reported.
export function describeUnbind({ removed = 0, durable = false } = {}) {
  if (Number(removed) > 0) return null;
  return {
    state: "removed-nothing",
    tone: "bad",
    text: durable
      ? "This tablet's binding was not there to remove — another device may be holding this shop's badge with a spare key. Use “Kill this shop's spare keys” to be sure."
      : "This tablet's binding was not there to remove — it may already have been moved or removed elsewhere.",
  };
}

// ---- the kill switch for one shop -------------------------------------------
// The only thing that actually cancels a device password once it has been used
// somewhere else, because it is keyed on the SHOP rather than on this tablet's own
// login (migration 1560). Every binding for that shop goes, every password that shop
// issued dies with it, and the shop's tablets fall back to being unbadged — which is
// how every tablet in the estate behaves today, so nothing breaks. An admin re-binds
// them afterwards.
//
// The refresh matters as much as it does on bind/unbind: this tablet's OWN binding
// may have just been deleted too, and nothing else re-mints the token.
export async function revokeShopDeviceKeys(
  siteKey,
  {
    rpc = (n, a) => supabase.rpc(n, a),
    refresh = () => supabase.auth.refreshSession(),
    storage = undefined,
  } = {}
) {
  const key = String(siteKey || "").trim().toLowerCase();
  if (!key) return { ok: false, error: "No shop chosen." };
  try {
    const { data, error } = await rpc("kiosk_revoke_site_bindings", { p: { site: key } });
    if (error) return { ok: false, error: friendlyError(error) };
    await refresh();   // this tablet's own badge may have gone with the rest
    // Its stored password is dead now too, so stop it being shown every morning.
    clearDeviceSecret(storage === undefined ? {} : { storage });
    return {
      ok: true,
      siteKey: data?.site_key || key,
      removed: Number(data?.removed || 0),
      passwordsKilled: Number(data?.passwords_killed || 0),
    };
  } catch (e) {
    return { ok: false, error: friendlyError(e) };
  }
}

// Turn a database refusal into something a shop manager can act on. Exported for
// the test; every branch is plain English, and it never leaks SQL at a tablet.
export function friendlyError(err) {
  const msg = String(err?.message || err || "").toLowerCase();
  if (msg.includes("locked") || msg.includes("tap an admin pin")) {
    return "Tap an admin PIN on this tablet first, then try again.";
  }
  if (msg.includes("revoke a shop")) {
    return "Only an admin can kill a shop's spare keys.";
  }
  if (msg.includes("only an admin")) {
    return "Only an admin can bind a tablet to a shop.";
  }
  // 1560's boot door refuses a tablet that is already bound somewhere else rather
  // than quietly deleting that binding to make room.
  if (msg.includes("already bound to a shop")) {
    return "This tablet is already bound to a shop — unbind it first.";
  }
  if (msg.includes("is not an active shop") || msg.includes("no shop given")
      || msg.includes("name one shop")) {
    return "That shop wasn't recognised.";
  }
  if (msg.includes("could not find the function") || msg.includes("pgrst202")) {
    return "This database hasn't had the binding update applied yet (migration 1460).";
  }
  return "Couldn't reach the database. Try again in a moment.";
}

// =============================================================================
// THE SPARE KEY — Lane CL, 16 Sep 2026. Everything below is what makes a badge
// survive a reload, and ALL of it is behind KIOSK_DURABLE_HOSTS, which is empty.
// =============================================================================

// EMPTY BY DEFAULT — no host, no password, no extra call, nothing to revert.
// Turning a host on is a reviewed one-line add after the runbook's soak, and it
// only does anything for a tablet an admin has already bound.
const KIOSK_DURABLE_HOSTS = new Set([
  // "trafford.eatrolled.com",   // ← one host at a time, after a shift's soak
]);

// The whole durable gate as a pure function, so the OFF path is provable without a
// browser. `query` is the value of ?kioskdurable= (or null).
export function decideKioskDurable({ hostname = "", query = null } = {}) {
  if (query === "on") return true;
  if (query === "off") return false;
  return KIOSK_DURABLE_HOSTS.has(String(hostname || "").toLowerCase());
}

export function kioskDurableEnabled() {
  if (typeof window === "undefined") return false; // node/tests: default off
  let q = null;
  try { q = new URLSearchParams(window.location.search).get("kioskdurable"); } catch (e) {}
  return decideKioskDurable({ hostname: window.location.hostname || "", query: q });
}

// ---- where the tablet keeps its password ------------------------------------
// One fixed key in this browser's own storage, on this tablet only. It never
// leaves the device except to be shown to the database, it is never written to a
// log or an error message, and every helper below is wrapped: a tablet in private
// mode, or with storage full or blocked, simply behaves as an unbound one.
export const DEVICE_SECRET_KEY = "rolled.kiosk.device.v1";

function defaultStorage() {
  try { return typeof window !== "undefined" ? window.localStorage : null; } catch (e) { return null; }
}

export function storeDeviceSecret(secret, { storage = defaultStorage() } = {}) {
  const s = String(secret || "").trim();
  if (!s || !storage) return false;
  try { storage.setItem(DEVICE_SECRET_KEY, s); return true; } catch (e) { return false; }
}

export function readDeviceSecret({ storage = defaultStorage() } = {}) {
  if (!storage) return null;
  try { return String(storage.getItem(DEVICE_SECRET_KEY) || "").trim() || null; } catch (e) { return null; }
}

// Deliberately reads before it removes, so on a host that was never durable there
// is nothing stored, nothing is read, and storage is not written to at all — the
// OFF path does not so much as touch it. It still runs on every unbind, gate or no
// gate, because a tablet that has been unbound must never keep a key to the door,
// even if the host was switched off in between.
export function clearDeviceSecret({ storage = defaultStorage() } = {}) {
  if (!storage) return false;
  try {
    if (!readDeviceSecret({ storage })) return false;
    storage.removeItem(DEVICE_SECRET_KEY);
    return true;
  } catch (e) { return false; }
}

// ---- ask the database for this tablet's password, and keep it ----------------
// Called right after a successful bind on a durable host. The database only opens
// this door for an admin PIN tapped on this very tablet, which we have just used to
// bind, so it is the one moment it can succeed. Never throws and never blocks the
// bind: if it fails the tablet is bound exactly as it is today, just not durably.
export async function issueAndStoreDeviceSecret({
  rpc = (n, a) => supabase.rpc(n, a),
  storage = defaultStorage(),
} = {}) {
  try {
    const { data, error } = await rpc("kiosk_issue_device_secret", {});
    if (error || !data || data.ok !== true || !data.secret) return false;
    return storeDeviceSecret(data.secret, { storage });
  } catch (e) {
    return false;
  }
}

// ---- the boot call: show the password, get the badge back --------------------
// The order matters and is asserted in the test: make sure we have a login first
// (a fresh page load has none), THEN show the password, THEN ask for a new token
// so it carries the badge. Nothing here throws: every failure returns null and the
// tablet carries on exactly as an unbound one does today, which is today's
// behaviour and fails open.
//
// A refusal deliberately leaves the stored password alone. We cannot tell "this
// password is dead" from "the network blinked" or "1560 is not on this database
// yet" without trusting the error text, and throwing the key away on a bad wifi
// morning would cost an admin a trip to the shop. A genuinely dead password simply
// keeps being refused, which is the same as having none.
export async function rebindFromStoredSecret({
  siteKey = null,
  durable = kioskDurableEnabled(),
  storage = defaultStorage(),
  ensureSession = () => ensureAnonSession(),
  rpc = (n, a) => supabase.rpc(n, a),
  refresh = () => supabase.auth.refreshSession(),
  getBound = () => currentBoundSite(),
} = {}) {
  try {
    if (!durable) return null;
    const key = String(siteKey ?? getActiveSiteKey() ?? "").trim().toLowerCase();
    if (!key) return null;
    const secret = readDeviceSecret({ storage });
    if (!secret) return null;
    await ensureSession();
    const { data, error } = await rpc("kiosk_rebind_site", { p: { secret, site: key } });
    if (error || !data || data.ok !== true) return null;
    await refresh();                 // the only re-mint there is — see bindThisTablet
    return await getBound();
  } catch (e) {
    return null;
  }
}

// The one extra line the admin panel shows on a durable host: does this tablet
// actually hold its spare key, or will it lose its badge the next time it reloads?
// Pure, so the wording is testable. Returns null on a host that is not durable —
// there is nothing to say there, and today that is every host.
//
// ⚠️ HOLDING A SPARE KEY IS NOT THE SAME AS THE KEY WORKING. (Review, 16 Sep 2026.)
// On boot rebindFromStoredSecret() shows the password and says which shop this
// SCREEN is set up as, and the database refuses the pair unless the binding is for
// that same shop. So a tablet badged for one shop while the screen is configured as
// another — the "mismatch" state describeBinding already has a red banner for —
// holds a key that will be refused every morning. Saying "it will keep its shop"
// there would be a green light for something that silently does not happen, so the
// mismatch is named instead. `siteKey` left out (the tests, and any caller that does
// not know the shop) behaves exactly as before.
export function describeDurable({ durable = false, bound = null, hasSecret = false, siteKey = "" } = {}) {
  if (!durable) return null;
  if (!bound) return { state: "n/a", text: "", tone: "warn" };
  const here = String(siteKey || "").trim().toLowerCase();
  const badge = String(bound || "").trim().toLowerCase();
  if (hasSecret && here && badge !== here) {
    return {
      state: "mismatch",
      text: `This tablet holds a spare key for ${badge}, but this screen is set up as ${here} — it will NOT get its shop back after a restart until it is re-bound.`,
      tone: "bad",
    };
  }
  if (hasSecret) {
    return { state: "durable", text: "This tablet will keep its shop after a restart.", tone: "good" };
  }
  return {
    state: "per-shift",
    text: "This tablet will LOSE its shop when it restarts — re-bind it to fix that.",
    tone: "warn",
  };
}

// Once per page load, whoever asks first. The panel calls it on mount; the module
// also kicks it off on import (below), which on both host screens is the very top
// of the file — before React has drawn anything.
let durableBadgeRun = null;
export function ensureDurableBadge(opts = {}) {
  if (!durableBadgeRun) durableBadgeRun = rebindFromStoredSecret(opts);
  return durableBadgeRun;
}
// Test seam only. Never called by the app.
export function resetDurableBadgeForTest() { durableBadgeRun = null; }

// 🟠 THE HONEST CAVEAT, so nobody is surprised by it. Getting the badge back takes
// a round trip, and the shop-scoped feeds do not wait for it. So the first few
// calls of a page load can still go out unbadged — and an unbadged call behaves
// exactly as every call behaves today: the feed answers whatever shop was asked
// for. It fails OPEN, never closed, so a till never shows an empty screen because
// of this. Everything after the rebind lands is badged. The admin panel shows the
// tablet as unbound (amber) for that moment and flips to bound on its own.
//
// On an OFF host — every host today — kioskDurableEnabled() is false, so this does
// nothing at all: no timer, no call, no storage read.
if (kioskDurableEnabled()) {
  try { Promise.resolve().then(() => ensureDurableBadge()); } catch (e) {}
}
